import { MediaMute, MediaPause, MediaPlay, MediaVolume } from '@tencent/odc-iconpack';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import './index.scss';

export interface IMediaPlayerProps {
  /**
   * 视频地址
   */
  video: string;
  /**
   * 视频封面
   */
  poster?: string;
  /**
   * 自定义类名
   */
  className?: string;
}

const MediaPlayer = (props: IMediaPlayerProps, ref: React.ForwardedRef<HTMLDivElement>) => {
  const { video, poster, className } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [volumeDragging, setVolumeDragging] = useState(false);
  const [progress, setProgress] = useState(0);

  // React 的 muted 属性在客户端渲染下不可靠，这里显式通过 DOM 属性设置，保证自动播放生效
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
    }
  }, []);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    // 浏览器自动播放策略可能拦截 play()（无用户手势时），catch 避免未处理的 Promise 拒绝
    if (el.paused) {
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.muted || el.volume === 0) {
      // 恢复上次音量；若音量曾被拖到 0，则恢复为 1
      el.muted = false;
      if (el.volume === 0) el.volume = 1;
      if (el.paused) {
        el.play().catch(() => {});
      }
    } else {
      el.muted = true;
    }
  };

  const seekFromClientX = useCallback((clientX: number) => {
    const el = videoRef.current;
    const bar = progressRef.current;
    if (!el || !bar || !el.duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    el.currentTime = ratio * el.duration;
    setProgress(ratio);
  }, []);

  const handleProgressPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    seekFromClientX(event.clientX);
  };

  const handleProgressPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    // 仅在按住拖拽时 seek
    if (event.buttons > 0) {
      seekFromClientX(event.clientX);
    }
  };

  const setVolumeFromClientY = useCallback((clientY: number) => {
    const el = videoRef.current;
    const bar = volumeRef.current;
    if (!el || !bar) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (rect.bottom - clientY) / rect.height));
    // 拖到 0 视为静音；拖到 >0 自动取消静音
    el.volume = ratio;
    el.muted = ratio === 0;
  }, []);

  const handleVolumePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setVolumeDragging(true);
    setVolumeFromClientY(event.clientY);
  };

  const handleVolumePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    // 仅在按住拖拽时调节
    if (event.buttons > 0) {
      setVolumeFromClientY(event.clientY);
    }
  };

  const handleVolumePointerEnd = () => setVolumeDragging(false);

  return (
    <div
      ref={ref}
      className={classNames('odc-media-player', { 'is-paused': !playing }, className)}
    >
      <video
        ref={videoRef}
        className="odc-media-player-video"
        src={video}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        onClick={togglePlay}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onVolumeChange={() => {
          const el = videoRef.current;
          if (!el) return;
          setMuted(el.muted);
          setVolume(el.volume);
        }}
        onTimeUpdate={() => {
          const el = videoRef.current;
          if (el && el.duration) {
            setProgress(el.currentTime / el.duration);
          }
        }}
      />
      <div className="odc-media-player-bar">
        <button
          type="button"
          className="odc-media-player-ctrl"
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? <MediaPause width={12} height={12} /> : <MediaPlay width={12} height={12} />}
        </button>
        <div
          ref={progressRef}
          className="odc-media-player-progress"
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          onPointerDown={handleProgressPointerDown}
          onPointerMove={handleProgressPointerMove}
        >
          <div className="odc-media-player-progress-track">
            <div className="odc-media-player-progress-fill" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
        <div className={classNames('odc-media-player-volume', { 'is-dragging': volumeDragging })}>
          <div className="odc-media-player-volume-panel">
            <div
              ref={volumeRef}
              className="odc-media-player-volume-track"
              role="slider"
              aria-label="Volume"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={muted ? 0 : Math.round(volume * 100)}
              onPointerDown={handleVolumePointerDown}
              onPointerMove={handleVolumePointerMove}
              onPointerUp={handleVolumePointerEnd}
              onLostPointerCapture={handleVolumePointerEnd}
            >
              {/* thumb 行程上下各内缩一个半径（10px 为 thumb 直径），保证圆块不超出滑轨 */}
              {/* fill 延伸至 thumb 圆心消除衔接断痕；音量为 0 时填充归零 */}
              <div
                className="odc-media-player-volume-fill"
                style={{ height: !muted && volume > 0 ? `calc(5px + ${volume} * (100% - 10px))` : '0px' }}
              />
              <div
                className="odc-media-player-volume-thumb"
                style={{ bottom: `calc(${muted ? 0 : volume} * (100% - 10px))` }}
              />
            </div>
          </div>
          <button
            type="button"
            className="odc-media-player-ctrl"
            onClick={toggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <MediaMute width={16} height={16} /> : <MediaVolume width={16} height={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.forwardRef(MediaPlayer);